import { useCallback } from 'react';
import { Dispatch } from 'react';
import { useToast } from 'framework/ui/Toast';
import { useEmployeeAvailabilityApi } from 'infrastructure/api';
import { FOCUS_SCHEDULES_RESOURCE_URL, FOCUS_TIME_LIMIT_ERROR_TEXT } from '../constants';
import { isFocusTimeLimitError, normalizeSlotsPayload, toFocusSchedulesRequestPayload } from '../helpers/helpers';
import { FocusTimeActionType, FocusTimeSchedulePayloadItem, FocusTimeSlot, SaveFocusTimeSlotsOptions } from '../types';

type FocusTimeSaveResponse = Readonly<{
  result: FocusTimeSchedulePayloadItem[];
}>;

type UseFocusTimePersistenceParams = Readonly<{
  dispatch: Dispatch<{ type: FocusTimeActionType.SetSlots; payload: FocusTimeSlot[] }>;
}>;

export const useFocusTimePersistence = ({ dispatch }: UseFocusTimePersistenceParams) => {
  const { sendPostRequest } = useEmployeeAvailabilityApi();
  const { error: showErrorToast, success: showSuccessToast } = useToast();

  const setSlots = useCallback(
    (nextSlots: FocusTimeSlot[]) => {
      dispatch({ type: FocusTimeActionType.SetSlots, payload: nextSlots });
    },
    [dispatch],
  );

  const saveSlots = useCallback(
    async (
      slots: FocusTimeSlot[],
      {
        showSuccessToast: shouldShowSuccessToast = true,
        showErrorToast: shouldShowErrorToast = true,
      }: SaveFocusTimeSlotsOptions = {},
    ) => {
      try {
        const response = await sendPostRequest(FOCUS_SCHEDULES_RESOURCE_URL, {
          schedules: toFocusSchedulesRequestPayload(slots),
        });
        const saveResponse = (await response.json()) as FocusTimeSaveResponse;

        const savedSlots = normalizeSlotsPayload(saveResponse.result);
        setSlots(savedSlots);

        if (shouldShowSuccessToast) showSuccessToast('Your focus time schedule has been saved successfully.');
      } catch (reason) {
        if (shouldShowErrorToast) {
          if (isFocusTimeLimitError(reason)) {
            showErrorToast(FOCUS_TIME_LIMIT_ERROR_TEXT, {
              title: 'Focus time limit exceeded while saving slots',
            });
          } else {
            showErrorToast('An error occurred while saving your focus time schedule. Please try again.', {
              title: 'Failed to update focus time slots',
            });
          }
        }

        throw reason;
      }
    },
    [sendPostRequest, setSlots, showErrorToast, showSuccessToast],
  );

  return {
    setSlots,
    saveSlots,
  };
};
